/**
 * Cookiebot consent helpers for gating non-essential tracking.
 * Necessary: JWT/session, device-uuid, Cookiebot itself.
 * Statistics: Datadog RUM, GA4, HubSpot analytics.
 * Marketing: Meta Pixel, Google Ads, HubSpot marketing.
 */

export type CookiebotConsent = {
  necessary?: boolean;
  preferences?: boolean;
  statistics?: boolean;
  marketing?: boolean;
};

type CookiebotApi = {
  consent?: CookiebotConsent;
  renew?: () => void;
};

function getCookiebot(): CookiebotApi | undefined {
  return (window as Window & { Cookiebot?: CookiebotApi }).Cookiebot;
}

/**
 * True when the user has accepted marketing cookies via Cookiebot.
 * @returns {boolean} Whether marketing consent is granted
 */
export function hasMarketingConsent(): boolean {
  return Boolean(getCookiebot()?.consent?.marketing);
}

/**
 * True when the user has accepted statistics/analytics cookies via Cookiebot.
 * @returns {boolean} Whether statistics consent is granted
 */
export function hasStatisticsConsent(): boolean {
  return Boolean(getCookiebot()?.consent?.statistics);
}

/**
 * Reopens the Cookiebot consent dialog so the user can change or withdraw
 * consent. Replaces the floating Cookiebot widget inside the logged-in portal.
 * @returns {boolean} False when Cookiebot has not loaded (e.g. blocked script)
 */
export function openCookieSettings(): boolean {
  const cookiebot = getCookiebot();
  if (typeof cookiebot?.renew !== "function") return false;
  cookiebot.renew();
  return true;
}
